import { useSelector, useDispatch } from 'react-redux'
import { useQuery } from '@tanstack/react-query'

import AnalysisToggles from '@App/components/AnalysisToggles/AnalysisToggles'
import DataSectionTitle from '@App/components/DataSectionTitle/DataSectionTitle'
import ResultToggles from '@App/components/ResultToggles/ResultToggles'
import SourceToggles from '@App/components/SourceToggles/SourceToggles'
import TimeSelector from '@App/components/TimeSelector/TimeSelector'
import { fetchLabResultSourcesByMission, fetchLabResultsByMission } from '@services/data.service'

import {
  setSelectionRange,
  setSourceActiveNames,
  setLabResultActiveAnalysisTypeIds,
  setLabResultActiveResultTypes,
} from '../lab-results.slice'

import './lab-results-filter.styl'

const EMPTY_RESULTS = []

function LabResultsFilter() {
  const dispatch = useDispatch()
  const {
    selectionRange,
    sourcesActiveNames,
    labResultActiveAnalysisTypeIds,
    labResultActiveResultTypes,
  } = useSelector(state => state.labResults)
  const { activeProject } = useSelector(state => state.missionSetup)

  const { data: sources } = useQuery({
    queryKey: ['labResultSources', activeProject?.id, selectionRange],
    queryFn: () => fetchLabResultSourcesByMission(
      activeProject?.id,
      selectionRange.startDate,
      selectionRange.endDate,
    ),
    enabled: !!activeProject?.id,
  })

  const { data: labResults = EMPTY_RESULTS } = useQuery({
    queryKey: ['labResultAnalysisTypes', activeProject?.id, selectionRange, sourcesActiveNames],
    queryFn: () => fetchLabResultsByMission(
      activeProject?.id,
      sourcesActiveNames,
      undefined,
      selectionRange.startDate,
      selectionRange.endDate,
    ),
    enabled: !!activeProject?.id,
  })

  const analysisTypes = [...labResults.reduce((types, sample) => {
    const analysis = Array.isArray(sample.properties?.analysis) ? sample.properties.analysis : []

    analysis.forEach(({ name, result }) => {
      if (!name) return

      const type = types.get(name) || { id: name, label: name, positive: 0, negative: 0 }
      if (result?.toLowerCase() === 'positive') type.positive += 1
      if (result?.toLowerCase() === 'negative') type.negative += 1
      types.set(name, type)
    })

    return types
  }, new Map()).values()]

  return (
    <div className="lab-results-filter">
      <DataSectionTitle>Select time window</DataSectionTitle>
      <TimeSelector
        selectionRange={selectionRange}
        setSelectionRange={v => dispatch(setSelectionRange(v))}
      />
      <DataSectionTitle>Available sources</DataSectionTitle>
      <SourceToggles
        sources={sources}
        sourcesActiveNames={sourcesActiveNames}
        setSourceActiveNames={setSourceActiveNames}
      />
      <DataSectionTitle>Result</DataSectionTitle>
      <ResultToggles
        activeResultTypes={labResultActiveResultTypes}
        setActiveResultTypes={setLabResultActiveResultTypes}
      />
      <DataSectionTitle>Analysis types</DataSectionTitle>
      <AnalysisToggles
        types={analysisTypes}
        activeTypeIds={labResultActiveAnalysisTypeIds}
        setActiveTypeIds={setLabResultActiveAnalysisTypeIds}
      />
    </div>
  )
}

export default LabResultsFilter
